import { useEffect, useState } from 'react';
import { Typography } from 'antd';
import {
  CheckOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { resolvePlanIcon, buildHeroGradient } from './planTheme';
import type { SubscriptionPlan, CurrentPlanStatus } from '../../types/business';

const { Text } = Typography;

const LOCALE_MAP: Record<string, string> = {
  uz: 'uz-UZ',
  ru: 'ru-RU',
  en: 'en-US',
};

function formatExpiresDate(iso: string, lang: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const locale = LOCALE_MAP[lang] ?? 'uz-UZ';
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

interface Props {
  plan: SubscriptionPlan;
  showActiveBadge: boolean;
  /** Short feature chips (e.g. "5 provayder · Cheksiz bron") */
  featureChips?: string[];
  /** ISO 8601 sana — tarif amal qilishi tugaydigan kun (BE'dan) */
  expiresAt?: string | null;
  /**
   * Joriy tarif holati. Undefined bo'lsa ACTIVE deb hisoblanadi
   * (eski BE bilan backward compatibility).
   */
  status?: CurrentPlanStatus;
  /** EXPIRED_IN_GRACE da grace deadline ISO. Boshqa hollarda null. */
  graceUntil?: string | null;
}

/**
 * Joriy tarif hero — plan rangida gradient, dekorativ blur orblar, plan
 * logosi katta, status badge, sana/countdown qatori, feature chips strip.
 *
 * Status-aware:
 *  - ACTIVE: yashil "Faol obuna" badge + "X gacha amal qiladi" pill.
 *  - EXPIRED_IN_GRACE: amber "Muddati tugagan" badge + "X da tugagan" +
 *    countdown ("2 kun 14 soat qoldi"), har daqiqa yangilanadi.
 *  - INACTIVE: amalda parent komponent banner ko'rsatadi; hero esa neytral
 *    ko'rinishda qoladi (badge yo'q).
 */
export default function CurrentPlanHero({
  plan,
  showActiveBadge,
  featureChips = [],
  expiresAt = null,
  status,
  graceUntil = null,
}: Props) {
  const { t } = useTranslation('owner');
  const icon = resolvePlanIcon(plan.theme.iconKind);
  const gradient = buildHeroGradient(plan.theme);

  const resolvedStatus: CurrentPlanStatus = status ?? 'ACTIVE';
  const isExpiredInGrace = resolvedStatus === 'EXPIRED_IN_GRACE';
  const isInactive = resolvedStatus === 'INACTIVE';

  const expiresLabel = expiresAt ? formatExpiresDate(expiresAt, i18n.language) : null;

  // Tick — har 60 sekundda countdown'ni yangilash uchun.
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!isExpiredInGrace || !graceUntil) return;
    const interval = window.setInterval(() => setNowTick(Date.now()), 60_000);
    return () => window.clearInterval(interval);
  }, [isExpiredInGrace, graceUntil]);

  const countdownLabel = (() => {
    if (!isExpiredInGrace || !graceUntil) return null;
    const target = new Date(graceUntil).getTime();
    if (Number.isNaN(target)) return null;
    const diffMs = target - nowTick;
    if (diffMs <= 0) return t('subscription.graceCountdown.ended');

    const totalMinutes = Math.floor(diffMs / 60_000);
    const totalHours = Math.floor(totalMinutes / 60);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours - days * 24;
    const minutes = totalMinutes - totalHours * 60;

    if (days > 0) {
      return t('subscription.graceCountdown.daysHours', { days, hours });
    }
    if (totalHours > 0) {
      return t('subscription.graceCountdown.hours', { hours: totalHours });
    }
    return t('subscription.graceCountdown.minutes', { minutes });
  })();

  // Status badge konfiguratsiyasi (header pill).
  const statusBadge = (() => {
    if (isExpiredInGrace) {
      return {
        label: t('subscription.statusBadge.expiredInGrace'),
        dotColor: '#fbbf24',
        dotGlow: 'rgba(251,191,36,0.32)',
        showIcon: true,
      };
    }
    if (isInactive) {
      return {
        label: t('subscription.statusBadge.inactive'),
        dotColor: '#f87171',
        dotGlow: 'rgba(248,113,113,0.32)',
        showIcon: true,
      };
    }
    if (showActiveBadge) {
      return {
        label: t('subscription.statusBadge.active'),
        dotColor: '#4ade80',
        dotGlow: 'rgba(74,222,128,0.32)',
        showIcon: false,
      };
    }
    return null;
  })();

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 20,
        overflow: 'hidden',
        background: gradient,
        boxShadow: `0 10px 28px ${plan.theme.accent}3a`,
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -80,
          right: -50,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.10)',
          filter: 'blur(8px)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: -60,
          left: -40,
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
          filter: 'blur(8px)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(at 20% 30%, rgba(255,255,255,0.12) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'relative',
          padding: '14px 18px 10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Text
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'rgba(255,255,255,0.78)',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}
        >
          {t('subscription.heroCaption')}
        </Text>

        {statusBadge && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              fontWeight: 700,
              color: '#fff',
              background: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.22)',
              borderRadius: 999,
              padding: '3px 10px',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              letterSpacing: 0.2,
              flexShrink: 0,
            }}
          >
            {statusBadge.showIcon ? (
              <ExclamationCircleFilled
                style={{ fontSize: 11, color: statusBadge.dotColor }}
              />
            ) : (
              <span
                aria-hidden
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: statusBadge.dotColor,
                  boxShadow: `0 0 0 3px ${statusBadge.dotGlow}`,
                }}
              />
            )}
            {statusBadge.label}
          </span>
        )}
      </div>

      <div
        style={{
          position: 'relative',
          padding: '4px 18px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: -5,
              borderRadius: 18,
              background: 'rgba(255,255,255,0.18)',
              filter: 'blur(4px)',
            }}
          />
          <div
            style={{
              position: 'relative',
              width: 56,
              height: 56,
              borderRadius: 16,
              background: 'rgba(255,255,255,0.22)',
              border: '2px solid rgba(255,255,255,0.42)',
              color: '#fff',
              fontSize: 24,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(0,0,0,0.20)',
            }}
          >
            {icon}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              display: 'block',
              fontSize: 22,
              fontWeight: 800,
              color: '#fff',
              letterSpacing: -0.4,
              lineHeight: 1.15,
            }}
          >
            {plan.displayName}
          </Text>
          {plan.subtitle && (
            <Text
              style={{
                display: 'block',
                fontSize: 13,
                color: 'rgba(255,255,255,0.78)',
                marginTop: 2,
                lineHeight: 1.4,
              }}
            >
              {plan.subtitle}
            </Text>
          )}

          {/* Sana qatori — ACTIVE'da "X gacha", EXPIRED_IN_GRACE'da "X da tugagan" */}
          {expiresLabel && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                marginTop: 8,
                padding: '3px 9px 3px 7px',
                borderRadius: 999,
                background: isExpiredInGrace
                  ? 'rgba(251,191,36,0.18)'
                  : 'rgba(255,255,255,0.16)',
                border: isExpiredInGrace
                  ? '1px solid rgba(251,191,36,0.35)'
                  : '1px solid rgba(255,255,255,0.22)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
              }}
            >
              <CalendarOutlined
                style={{
                  fontSize: 10,
                  color: isExpiredInGrace ? '#fef3c7' : 'rgba(255,255,255,0.85)',
                }}
              />
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: isExpiredInGrace ? '#fef3c7' : 'rgba(255,255,255,0.92)',
                  letterSpacing: 0.1,
                }}
              >
                {isExpiredInGrace
                  ? t('subscription.expiredAt', { date: expiresLabel })
                  : t('subscription.expiresAt', { date: expiresLabel })}
              </Text>
            </div>
          )}

          {/* Countdown qatori — faqat EXPIRED_IN_GRACE'da */}
          {countdownLabel && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                marginTop: 6,
                marginLeft: expiresLabel ? 6 : 0,
                padding: '3px 9px 3px 7px',
                borderRadius: 999,
                background: 'rgba(248,113,113,0.22)',
                border: '1px solid rgba(248,113,113,0.40)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
              }}
            >
              <ClockCircleOutlined style={{ fontSize: 10, color: '#fecaca' }} />
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#fecaca',
                  letterSpacing: 0.1,
                }}
              >
                {countdownLabel}
              </Text>
            </div>
          )}
        </div>
      </div>

      {featureChips.length > 0 && (
        <div
          style={{
            position: 'relative',
            background: 'rgba(0,0,0,0.18)',
            padding: '10px 16px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          {featureChips.map((chip, i) => (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11,
                fontWeight: 600,
                color: '#fff',
                background: 'rgba(255,255,255,0.14)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 999,
                padding: '3px 9px',
              }}
            >
              <CheckOutlined style={{ fontSize: 9, opacity: 0.78 }} />
              {chip}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
